import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { toast } from "react-toastify";
import FileUpload, { AcceptedFileTypes, SelectedDocument } from "./FileUpload";
import { EndpointType } from "@/constants/api";

interface Payload {
  folder_id: number;
  file_name: string;
  document_master_id?: number;
  size: number;
  mark_as_na?: boolean;
  s3_key: string;
  version_id: string;
}

interface S3FileUploadProps {
  label: string;
  folderId: number;
  documentTypeId?: number;
  accept?: AcceptedFileTypes[];
  maxFileSize?: number;
  id: string;
  isNA?: boolean;
  disableIsNA?: boolean;
  isDriveUpload?: boolean;
  isUpdate?: boolean;
  updateId?: number;
  selectedDocument?: SelectedDocument;
  onNAChange?: (isNA: boolean) => void;
  onUploadComplete?: (fileData: {
    folder_id: number;
    filename: string;
    s3_key: string;
    version_id: string;
  }) => void;
  onUploadError?: (error: string) => void;
  setForceRefresh?: (forceRefresh: boolean) => void;
  onSuccessUploadCallback?: (payload: Payload) => void;
  endpoint?: string;
}

const S3FileUpload: React.FC<S3FileUploadProps> = ({
  label,
  folderId,
  endpoint = "",
  documentTypeId,
  accept,
  maxFileSize,
  id,
  isNA,
  disableIsNA,
  isDriveUpload = false,
  isUpdate = false,
  updateId,
  selectedDocument,
  onNAChange,
  onUploadComplete,
  onUploadError,
  setForceRefresh,
  onSuccessUploadCallback,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string>("");
  const [cancelUpload, setCancelUpload] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<XMLHttpRequest | null>(
    null,
  );
  const handleFileChange = useCallback(async (newFile: File | null) => {
    setError("");
    setFile(newFile);
    setCancelUpload(false);
  }, []);

  const handleUploadingFile = useCallback(async () => {
    if (!file) return;
    try {
      // Step 1: Get presigned URL
      setIsUploading(true);
      const { authenticatedCall: getPresignedUrl } =
        actionsHandler("filesUpload");
      const presignedUrlResponse = await dispatch(
        getPresignedUrl({
          method: "post",
          payload: {
            folder_id: folderId,
            filename: file.name,
          },
        }),
      );

      if (!presignedUrlResponse.ok) {
        throw new Error("Failed to get upload URL");
      }

      const { url, s3_key } = presignedUrlResponse.data;

      // Step 2: Upload file to S3
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      };

      xhr.onload = async () => {
        if (xhr.status === 200) {
          try {
            // Step 3: Save file metadata
            handleSuccessFullUpload(
              file,
              s3_key,
              xhr.getResponseHeader("x-amz-version-id") || "",
            );
          } catch (error) {
            handleError(
              error instanceof Error
                ? error.message
                : "Failed to save file metadata",
            );
          }
        } else {
          handleError("Failed to upload file to S3");
        }
        setIsUploading(false);
      };

      xhr.onerror = () => {
        handleError("Network error occurred during upload");
        setIsUploading(false);
      };

      setCurrentRequest(xhr);
      xhr.send(new Blob([file]));
    } catch (error) {
      handleError(error instanceof Error ? error.message : "Upload failed");
    }
  }, [file, isDriveUpload, folderId, documentTypeId]);

  const handleSaveFile = useCallback(
    async (payload: Payload) => {
      const { authenticatedCall: saveFile } = actionsHandler(
        (endpoint as EndpointType) ||
          (isDriveUpload ? "myDriveFiles" : "fileSave"),
      );
      const saveResponse = await dispatch(
        saveFile({
          method: isUpdate ? "put" : "post",
          payload: payload,
          customParams: updateId ? [String(updateId) + "/"] : undefined,
        }),
      );

      if (saveResponse.ok) {
        toast.success(
          saveResponse.data.message || "File uploaded successfully",
        );
        onUploadComplete?.(saveResponse.data);
      } else {
        throw new Error(
          saveResponse.data.message || "Failed to save file metadata",
        );
      }
    },
    [isDriveUpload],
  );

  const handleSuccessFullUpload = useCallback(
    async (file: File, s3_key: string, version_id: string) => {
      if (!file) return;

      const payload: Payload = {
        folder_id: folderId,
        file_name: file.name,
        s3_key: s3_key,
        version_id: version_id,
        size: file.size,
      };

      if (documentTypeId) {
        payload["document_master_id"] = documentTypeId;
      }
      if (onSuccessUploadCallback) {
        onSuccessUploadCallback(payload);
      } else {
        handleSaveFile(payload);
      }
    },
    [handleSaveFile, onUploadComplete],
  );

  useEffect(() => {
    if (file) {
      handleUploadingFile();
    }
  }, [handleUploadingFile]);

  useEffect(() => {
    if (cancelUpload) {
      currentRequest?.abort();
      setCancelUpload(false);
      setUploadProgress(0);
      setIsUploading(false);
      setFile(null);
      onUploadError?.("Upload cancelled");
      toast.warning("Upload cancelled");
    }
  }, [cancelUpload]);

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setUploadProgress(0);
    setFile(null);
    onUploadError?.(errorMessage);
    toast.error(errorMessage);
  };

  const handleSavingMarkAsNA = useCallback(
    async (type: "user" | "default" = "default", isNA: boolean) => {
      setFile(null);
      setError("");
      setUploadProgress(0); // Reset upload progress
      if (type === "user" && isNA) {
        const { authenticatedCall: saveFile } = actionsHandler(
          isDriveUpload ? "myDriveFiles" : "fileSave",
        );

        const saveResponse = await dispatch(
          saveFile({
            method: "post",
            payload: {
              folder_id: folderId,
              file_name: null,
              s3_key: null,
              version_id: null,
              document_master_id: documentTypeId,
              size: null,
              mark_as_na: isNA,
            },
          }),
        );
        if (saveResponse.ok) {
          toast.success(saveResponse.data.message);
          setForceRefresh?.(true);
        }
      }
      onNAChange?.(isNA);
    },
    [
      isDriveUpload,
      documentTypeId,
      folderId,
      dispatch,
      onNAChange,
      setForceRefresh,
    ],
  );

  return (
    <FileUpload
      label={label}
      file={file}
      onFileChange={handleFileChange}
      error={error}
      accept={accept}
      maxFileSize={maxFileSize}
      id={id}
      uploadProgress={uploadProgress}
      setUploadProgress={setUploadProgress}
      isNA={isNA}
      disableIsNA={disableIsNA}
      onNAChange={handleSavingMarkAsNA}
      selectedDocument={selectedDocument}
      setCancelUpload={setCancelUpload}
      isUploading={isUploading}
      setIsUploading={setIsUploading}
    />
  );
};

export default S3FileUpload;
